import React, { CSSProperties, forwardRef, ReactElement, useCallback, useMemo } from 'react';
import { useButton, useHover } from 'react-aria';
import { FocusableProps, HoverEvents, PressEvent } from '@react-types/shared';
import { useLogger, Loggable } from '@direct-frontend/modules-logger';
import { genericMemo } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { useObjectRef } from '../../hooks/useObjectRef';
import './ClickableIcon.css';
import { TestProps } from '../../internal/types/testTypes';
import { PressProps } from '../../internal/types/pressTypes';
import { IconColor, IconRole } from '../../icons';
import { useFocusOptions } from '../../hooks/useFocusOptions';

const cls = cn('ClickableIcon');

export interface ClickableIconProps extends PressProps, FocusableProps, HoverEvents, TestProps, Loggable {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Иконка с заданным размером
     */
    children: ReactElement<SVGElement>;

    /**
     * Цветовое оформление кнопки в соответствии с дизайн-системой.
     * При `current` используется значение `icon-main`/`icon-supplementary` из текущего цветового скина
     * @default gray
     */
    color?: IconColor | 'current';

    /**
     * Роль элемента: основной (main) или второстепенный (supplementary).
     * От роли зависят цветовые стили элемента
     * @default 'supplementary'
     */
    role?: IconRole;

    /**
     * Размер кликабельной области в пикселях (высота = ширине).
     * Если не задавать в явном виде - будет равен размеру иконки.
     */
    clickableAreaSize?: string;

    /**
     * Признак отключения кнопки
     * @default false
     */
    isDisabled?: boolean;

    /**
     * Обработчик нажатия на кнопку
     */
    onClick?: () => void;

    /**
     * Текст во всплывающей подсказке
     */
    title?: string;

    /**
     * Подпись к кнопке-иконке для screen-readers
     * @default title
     */
    'aria-label'?: string;

    /**
     * Параметр участия в последовательной навигации
     */
    tabIndex?: number;

    /**
     * Тег для компонента
     * @default button
     */
    as?: 'button' | 'span';
}

/**
 * Компонент для создания кнопок-иконок
 */
export const ClickableIcon = genericMemo(
    forwardRef<HTMLButtonElement, ClickableIconProps>(function ClickableIcon(props, forwardedRef) {
        const {
            clickableAreaSize,
            color = 'gray',
            role = 'supplementary',
            isDisabled = false,
            className,
            children,
            title,
            onPress,
            tabIndex,
            as: ElementTag = 'button',
            'data-testid': dataTestId = 'ClickableIcon',
        } = props;

        const { onClick, logNode, ...restProps } = props;

        const { LogProvider, logClick } = useLogger('ClickableIcon', logNode);
        const ref = useObjectRef(forwardedRef);

        const onClickHandler = useCallback(
            (event: PressEvent) => {
                logClick();

                if (onPress) {
                    onPress(event);
                } else {
                    onClick?.();
                }
            },
            [logClick, onClick, onPress],
        );

        const { buttonProps, isPressed } = useButton(
            {
                ...restProps,
                onPress: onClickHandler,
                elementType: ElementTag,
            },
            ref,
        );
        const { hoverProps, isHovered } = useHover(props);
        // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
        const { focusProps, isFocusVisible } = useFocusOptions();

        const style = useMemo<CSSProperties>(
            () => ({
                width: clickableAreaSize && `${clickableAreaSize}px`,
                height: clickableAreaSize && `${clickableAreaSize}px`,
            }),
            [clickableAreaSize],
        );

        return (
            <LogProvider>
                <ElementTag
                    {...buttonProps}
                    {...hoverProps}
                    {...focusProps}
                    ref={ref}
                    data-testid={dataTestId}
                    title={title}
                    aria-label={props['aria-label'] || title}
                    tabIndex={tabIndex}
                    style={style}
                    className={cls(
                        {
                            color,
                            role,
                            hovered: isHovered,
                            pressed: isPressed,
                            disabled: isDisabled,
                            focused: isFocusVisible,
                        },
                        [className],
                    )}
                >
                    {children}
                </ElementTag>
            </LogProvider>
        );
    }),
);
